import React, { useRef, useState } from 'react';
import { Page } from 'react-pdf';

type PdfPagesProps = {
    pages: number;
    page: number;
    scale: number;
    pdfWidth: number;
    rotate: number;
    isVerticalScroll: boolean;
    isResponsive: boolean;
};

const PdfPages: React.FunctionComponent<PdfPagesProps> = ({
    pages,
    page,
    scale,
    pdfWidth,
    rotate,
    isVerticalScroll,
    isResponsive,
}) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    // Track each page's intrinsic rotation from the PDF metadata.
    // react-pdf's `rotate` prop *replaces* the PDF's built-in rotation, so we must
    // read it on load and add the user's rotation on top to preserve correct orientation.
    const [intrinsicRotations, setIntrinsicRotations] = useState<Record<number, number>>({});

    // Set width and heigth to 100% is Pdf isResponsive
    const handleRenderSuccess = () => {
        if (isResponsive && canvasRef.current) {
            canvasRef.current.style.setProperty('width', `${100 * scale}%`);
            canvasRef.current.style.setProperty('height', 'auto');
        }
    };

    // Number of pages of the documents transform as array in order to display all the pages in isVerticalScroll mode
    // Or only the current page if isVerticalScroll is false
    const pageNumbers =
        isVerticalScroll && pages > 0 ? Array.from({ length: pages }, (_, i) => i + 1) : [page];

    return pageNumbers.map((nbr) => {
        const intrinsicRotation = intrinsicRotations[nbr];
        // Only pass rotate after we know the intrinsic value; until then let react-pdf
        // use the PDF's own rotation so the first render is already correct.
        const effectiveRotate =
            intrinsicRotation !== undefined
                ? (((intrinsicRotation + rotate) % 360) + 360) % 360
                : undefined;

        return (
            <Page
                key={nbr}
                pageNumber={nbr}
                scale={scale}
                width={pdfWidth}
                rotate={effectiveRotate}
                canvasRef={canvasRef}
                onLoadSuccess={(pdfPage) => {
                    setIntrinsicRotations((prev) => ({
                        ...prev,
                        [nbr]: pdfPage.rotate ?? 0,
                    }));
                }}
                onRenderSuccess={() => handleRenderSuccess()}
            />
        );
    });
};

export default PdfPages;
